/**
 * Maintains a held-key set keyed by e.code (layout-independent — KeyW works on
 * AZERTY). Cleared on blur/visibilitychange (the stuck-key-after-alt-tab bug).
 */
export class Keyboard {
  private held = new Set<string>();
  /** Set true by any keydown; InputManager reads+clears for device detection. */
  activity = false;

  attach(target: Window): void {
    target.addEventListener('keydown', this.onKeyDown);
    target.addEventListener('keyup', this.onKeyUp);
    target.addEventListener('blur', this.clear);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.clear();
    });
    // Steam Input can flip a pad in/out mid-session (Deck mode switches) —
    // treat it like a focus change so no emulated key sticks held.
    target.addEventListener('gamepaddisconnected', this.clear);
  }

  isHeld(code: string): boolean {
    return this.held.has(code);
  }

  anyHeld(codes: readonly string[]): boolean {
    for (const c of codes) if (this.held.has(c)) return true;
    return false;
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    // Tab is a game key (overview panel). Without this — and it must run
    // before the repeat early-return — Tab moves browser focus off the
    // window, and the blur handler wipes all held keys mid-play.
    if (e.code === 'Tab') e.preventDefault();
    if (e.repeat) return;
    this.held.add(e.code);
    this.activity = true;
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.held.delete(e.code);
  };

  private clear = (): void => {
    this.held.clear();
  };
}
